import React, {createContext, memo, useContext} from 'react';
import {
  type ApolloError,
  type QueryHookOptions as ApolloQueryOptions,
  useQuery,
  gql,
} from '@apollo/client';
import type {
  DeliveryManagementDeliveryContextDeliveryQuery,
  DeliveryManagementDeliveryContextDeliveryQueryVariables,
  DeliveryManagementDeliveryContextDeliveryQuery_deliveryByOrderNumber,
} from '../../__generated__/DeliveryManagementDeliveryContextDeliveryQuery';
import {deliveryTrackingLinkFragment} from './TrackingLink/utilities/showTrackingLink';

export const DeliveryContext = createContext<{
  loading: boolean;
  delivery: DeliveryManagementDeliveryContextDeliveryQuery_deliveryByOrderNumber | null | undefined;
  error?: ApolloError;
}>({
  loading: true,
  delivery: null,
  error: undefined,
});

export const useDelivery = () => {
  return useContext(DeliveryContext);
};

type QueryOptions = Omit<
  ApolloQueryOptions<
    DeliveryManagementDeliveryContextDeliveryQuery,
    DeliveryManagementDeliveryContextDeliveryQueryVariables
  >,
  'variables'
>;

type DeliveryProviderProps = QueryOptions & {
  children: React.ReactNode;
  variables: DeliveryManagementDeliveryContextDeliveryQueryVariables;
};

export const DeliveryProvider = ({children, variables, ...queryOptions}: DeliveryProviderProps) => {
  const queryVariables = {
    orderNumber: variables.orderNumber,
    includeDeliveryTrackingData: !!variables.includeDeliveryTrackingData,
  };
  const deliveryQueryResult = useQuery<
    DeliveryManagementDeliveryContextDeliveryQuery,
    DeliveryManagementDeliveryContextDeliveryQueryVariables
  >(DeliveryQuery, {
    errorPolicy: 'all',
    variables: queryVariables,
    ...queryOptions,
  });

  return (
    <DeliveryContext.Provider
      value={{
        loading: deliveryQueryResult.loading,
        error: deliveryQueryResult.error,
        delivery: deliveryQueryResult.data?.deliveryByOrderNumber,
      }}
    >
      {children}
    </DeliveryContext.Provider>
  );
};

export const DeliveryQuery = gql`
  query DeliveryManagementDeliveryContextDeliveryQuery(
    $orderNumber: String!
    $includeDeliveryTrackingData: Boolean!
  ) {
    deliveryByOrderNumber(orderNumber: $orderNumber) {
      id
      assignable
      driver {
        id
        fullName
      }
      ...DeliveryTrackingLinkFragment @include(if: $includeDeliveryTrackingData)
    }
  }
  ${deliveryTrackingLinkFragment.delivery}
`;

export default memo(DeliveryProvider);
